import BigNumber from 'bignumber.js';

import type {
  ApiLiquidityHubYieldGroup,
  LiquidityHubYieldGroup,
  LiquidityHubYieldGroupType,
  Token,
} from 'types';
import { convertMantissaToTokens } from 'utilities/convertMantissaToTokens';
import convertUsdMantissaToCents from 'utilities/convertUsdMantissaToCents';
import { formatToLiquidityHubResource } from './formatToLiquidityHubResource';

export const formatToLiquidityHubYieldGroup = ({
  apiYieldGroup,
  tokens,
  underlyingToken,
  tokenPriceCents,
}: {
  apiYieldGroup: ApiLiquidityHubYieldGroup;
  tokens: Token[];
  underlyingToken: Token;
  tokenPriceCents: BigNumber;
}): LiquidityHubYieldGroup => {
  // TODO: use translation keys
  const defaultNameByType: Record<LiquidityHubYieldGroupType, string> = {
    core: 'Venus Core',
    flux: 'Venus Flux',
    frv: 'Fixed Rate Vaults',
  };

  const type: LiquidityHubYieldGroupType = apiYieldGroup.kind ?? 'core';

  const sources = apiYieldGroup.resources.map(apiResource =>
    formatToLiquidityHubResource({
      apiResource,
      tokens,
      underlyingToken,
      tokenPriceCents,
    }),
  );

  let bgClassName = 'bg-blue';

  if (apiYieldGroup.kind === 'flux') {
    bgClassName = 'bg-yellow';
  } else if (apiYieldGroup.kind === 'frv') {
  }

  const allocationTokens = convertMantissaToTokens({
    value: new BigNumber(apiYieldGroup.totalUnderlyingMantissa),
    token: underlyingToken,
  });

  const allocationCentsFromApi = convertUsdMantissaToCents(
    apiYieldGroup.totalUnderlyingUsdMantissa ?? 0,
  );

  const allocationCents = apiYieldGroup.totalUnderlyingUsdMantissa
    ? allocationCentsFromApi
    : allocationTokens.multipliedBy(tokenPriceCents);

  const allocationCapTokens = convertMantissaToTokens({
    value: new BigNumber(apiYieldGroup.effectiveCapMantissa),
    token: underlyingToken,
  });

  const allocationCapCentsFromApi = convertUsdMantissaToCents(
    apiYieldGroup.absoluteCapUsdMantissa ?? 0,
  );
  const allocationCapCents = apiYieldGroup.absoluteCapUsdMantissa
    ? allocationCapCentsFromApi
    : allocationCapTokens.multipliedBy(tokenPriceCents);

  const liquidityTokens = convertMantissaToTokens({
    value: new BigNumber(apiYieldGroup.maxWithdrawMantissa),
    token: underlyingToken,
  });
  const liquidityCents = liquidityTokens.multipliedBy(tokenPriceCents);

  return {
    address: apiYieldGroup.yieldGroupAddress,
    type,
    name: defaultNameByType[type],
    bgClassName,
    allocationTokens,
    allocationCents,
    allocationCapPercentage: new BigNumber(apiYieldGroup.percentageCapRatio).multipliedBy(100),
    allocationCapTokens,
    allocationCapCents,
    liquidityTokens,
    liquidityCents,
    averageSupplyApyPercentage: new BigNumber(apiYieldGroup.spotApyRatio).multipliedBy(100),
    paused: apiYieldGroup.isPaused,
    sources,
  };
};
